// SEQ-180 E14 filter bar state and date-range resolution, split out of
// PnlAnalyticsFilterBar.tsx (a plain module, not a component file) so that
// component file only exports the component
// (react-refresh/only-export-components).
import { datePreset, type DatePresetName, type DateRange } from "../../lib/pnl/fiscalYear";

export interface PnlProjectOption {
  id: string;
  name: string;
  status: string;
}

export interface PnlAnalyticsFilterState {
  preset: DatePresetName;
  /** 'YYYY-MM', only meaningful when preset === 'custom'. */
  customFrom: string | null;
  customTo: string | null;
  projectIds: string[];
  statuses: string[];
  /**
   * SEQ-180 phase 2 (plan decision D19): the id of one of the open
   * dashboard's own saved date presets. When set it wins over `preset`, and
   * it is cleared the moment the user picks a built-in preset instead. A
   * preset that has since been deleted, or a dashboard that never had it,
   * falls back to `preset` rather than blanking the page.
   */
  customPresetId?: string | null;
}

export const DEFAULT_PNL_FILTER_STATE: PnlAnalyticsFilterState = {
  preset: "financial_year_to_date",
  customFrom: null,
  customTo: null,
  projectIds: [],
  statuses: [],
  customPresetId: null,
};

/** A dashboard-scoped named range, stored on `pnl_dashboards.date_presets` (D19). */
export interface PnlSavedDatePreset {
  id: string;
  name: string;
  from: string;
  to: string;
}

function monthInputToRange(customFrom: string | null, customTo: string | null): DateRange | undefined {
  if (!customFrom || !customTo) return undefined;
  const [toYear, toMonth] = customTo.split("-").map(Number);
  const lastDay = new Date(toYear, toMonth, 0).getDate();
  return { from: `${customFrom}-01`, to: `${customTo}-${String(lastDay).padStart(2, "0")}` };
}

/** Resolves the filter bar's state to a concrete { from, to } for the RPC. */
export function resolvePnlDateRange(
  state: PnlAnalyticsFilterState,
  todayIso: string,
  savedPresets: PnlSavedDatePreset[] = [],
): DateRange {
  if (state.customPresetId) {
    const saved = savedPresets.find((p) => p.id === state.customPresetId);
    if (saved) return { from: saved.from, to: saved.to };
  }
  if (state.preset === "custom") {
    const custom = monthInputToRange(state.customFrom, state.customTo);
    if (!custom) return { from: null, to: null };
    return datePreset("custom", todayIso, custom);
  }
  return datePreset(state.preset, todayIso);
}
